import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppStateSignupSelector, setEmail, setPassword, setSignupError, setConfirmEmail, setConfirmPassword, clearSignup } from './signupSlice'
import styles from './Signup.module.scss';
import { Input, Message } from 'semantic-ui-react';
import { useHistory, NavLink } from 'react-router-dom';
import { Reasoning, ErrorValidationLabel } from '../../components/errorValidationLabel/ErrorValidationLabel';
import { signup } from './signupActionCreators';
import { Button } from '../../components/button/Button';
import { Spinner } from '../../components/spinner/Spinner';
import { useTranslation } from 'react-i18next';

export function Signup() {
    const { email, confirmEmail, password, confirmPassword, signupError, loading } = useAppStateSignupSelector(state => state);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const history = useHistory();

    const [invalidEmail, setInvalidEmail] = useState<Reasoning | null>(null);
    const [emailMatchError, setEmailsMatch] = useState<Reasoning | null>(null);
    const [invalidPassword, setInvalidPassword] = useState<Reasoning | null>(null);
    const [invalidPasswordRequirements, setInvalidPasswordRequirements] = useState<string[]>([]);
    const [passwordMatchError, setPasswordsMatch] = useState<Reasoning | null>(null);

    useEffect(() => {
        dispatch(clearSignup(null));
        clearValidators();
    }, [dispatch])

    const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        let valid = validateForm();
        dispatch(setSignupError(null));
        if (valid) {
            dispatch(signup(email, password, navigateToDashboard));
        }
    }

    const navigateToDashboard = () => {
        history.push('/dashboard');
    }

    const validateEmail = (value: string) => {
        // eslint-disable-next-line no-useless-escape
        const emailRegex = new RegExp("^[^@]+@[^@]+\.[^@]+$");

        if (value.length === 0) {
            setInvalidEmail('Required');
            return false;
        } else if (value.match(emailRegex) === null) {
            setInvalidEmail('Invalid');
            return false;
        }

        setInvalidEmail(null);
        return true;
    }

    const validatePassword = (value: string) => {
        const passwordRegex = new RegExp("^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{8,}$");

        const errors = [];

        if (value.length < 8) {
            // remove the requirement from the list
            errors.push('ErrorValidation.MinimumEightCharacters');
        }

        if (!value.match(/[A-Z]/)) {
            errors.push('ErrorValidation.OneUpperCaseLetter');
        }

        if (!value.match(/[a-z]/)) {
            errors.push('ErrorValidation.OneLowerCaseLetter');
        }

        if (!value.match(/[0-9]/)) {
            errors.push('ErrorValidation.OneNumber');
        }

        if (!value.match(/[!@#$%^&*()-=_+]/)) {
            errors.push('ErrorValidation.OneSpecialCharacter');
        }

        setInvalidPasswordRequirements(errors);

        if (value.length === 0) {
            setInvalidPassword('Required');
            return false;
        } else if (value.match(passwordRegex) === null) {
            setInvalidPassword('RequirementsNotMet');
            return false;
        }

        setInvalidPassword(null);
        return true;
    }

    const validateConfirmEmail = (value: string) => {
        if (email !== value) {
            setEmailsMatch('Match');
            return false;
        }

        setEmailsMatch(null);
        return true;
    }

    const validateConfirmPassword = (value: string) => {
        if (password !== value) {
            setPasswordsMatch('Match');
            return false;
        }

        setPasswordsMatch(null);
        return true;
    }

    const validateForm = () => {
        clearValidators();
        let validForm = true;

        if (!validateEmail(email)) {
            validForm = false;
        }

        if (!validatePassword(password)) {
            validForm = false;
        }

        //Kick out before match checking if either email or password is missing
        if (!validForm) return validForm;

        if (!validateConfirmEmail(confirmEmail)) {
            validForm = false;
        } 

        if (!validateConfirmPassword(confirmPassword)) {
            validForm = false;
        }

        return validForm;
    }

    const clearValidators = () => {
        setInvalidEmail(null);
        setInvalidPassword(null);
        setEmailsMatch(null);
        setPasswordsMatch(null);
    }

    return (
        <div className={styles.page}>
            <div className={styles.form}>
                {signupError ? 
                    <div className={styles.row}>
                        <Message negative>
                            <Message.Header>{t(signupError)}</Message.Header>
                        </Message>
                    </div>
                : null }
                <div className={styles.row}>
                    <div className='headerContainer'>
                        <h1>Signup</h1>
                        <Spinner isLoading={loading} />
                    </div>
                </div>
                <form onSubmit={handleSignup}>
                    <div className={styles.row}>
                        <label htmlFor="signup-email">{t('Login.EmailAddress')}</label>
                        <Input
                            id="signup-email"
                            value={email}
                            onChange={(e: any) => {
                                dispatch(setEmail(e.target.value));
                                validateEmail(e.target.value);
                            }}
                            type="email"
                            autoComplete="email"
                            aria-describedby={invalidEmail ? 'signup-email-error' : undefined}
                            aria-invalid={!!invalidEmail}
                        />
                        {invalidEmail ? <ErrorValidationLabel fieldDescription={t('Login.EmailAddress')} reasoning={invalidEmail} id="signup-email-error" /> : null}
                    </div>           
                    <div className={styles.row}>
                        <label htmlFor="signup-confirm-email">{t('Login.ConfirmEmailAddress')}</label>
                        <Input
                            id="signup-confirm-email"
                            value={confirmEmail}
                            onChange={(e: any) => {
                                dispatch(setConfirmEmail(e.target.value));
                                validateConfirmEmail(e.target.value);
                            }}
                            type="email"
                            autoComplete="email"
                            aria-describedby={emailMatchError ? 'signup-confirm-email-error' : undefined}
                            aria-invalid={!!emailMatchError}
                        />
                        {emailMatchError ? <ErrorValidationLabel fieldDescription={t('Login.EmailAddress')} reasoning={emailMatchError} id="signup-confirm-email-error" /> : null}
                    </div>
                    <div className={styles.row}>
                        <label htmlFor="signup-password">{t('Login.Password')}</label>
                        <Input
                            id="signup-password"
                            value={password}
                            onChange={(e: any) => {
                                dispatch(setPassword(e.target.value));
                                validatePassword(e.target.value);
                            }}
                            type="password"
                            autoComplete="new-password"
                            aria-describedby={invalidPassword ? 'signup-password-error' : undefined}
                            aria-invalid={!!invalidPassword}
                        />
                        {invalidPassword ?
                            <ErrorValidationLabel
                                fieldDescription={t('Login.Password')}
                                reasoning={invalidPassword}
                                requirements={invalidPasswordRequirements}
                                id="signup-password-error"
                            />
                        : null}
                    </div>
                    <div className={styles.row}>
                        <label htmlFor="signup-confirm-password">{t('Login.ConfirmPassword')}</label>
                        <Input
                            id="signup-confirm-password"
                            value={confirmPassword}
                            onChange={(e: any) => {
                                dispatch(setConfirmPassword(e.target.value));
                                validateConfirmPassword(e.target.value);
                            }}
                            type="password"
                            autoComplete="new-password"
                            aria-describedby={passwordMatchError ? 'signup-confirm-password-error' : undefined}
                            aria-invalid={!!passwordMatchError}
                        />
                        {passwordMatchError ? <ErrorValidationLabel fieldDescription={t('Login.Password')} reasoning={passwordMatchError} id="signup-confirm-password-error" /> : null}
                    </div>
                    <div className={styles.row}>
                        <Button color="blue" type="submit" data-testid="signupButton">
                            {t('Login.Signup')}
                        </Button>
                    </div>
                </form>
                <div className={styles.row}>
                    <NavLink exact to="/login" className={`${styles.center} ${styles.returnToLogin}`}>Return to Login</NavLink>
                </div>
            </div>
        </div>
    );
}